import { Box, Card, Container, Paper, Stack, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React from 'react';

export interface ILandingPageStep {
    id: string;
    title: string;
    description: string;
    icon: React.ReactNode;
}

export interface ILandingPageFeatureCard {
    id: string;
    title: string;
    description: string;
    icon: React.ReactNode;
    imagePath?: string;
    onClick: () => void;
}

export interface ILandingPageQuickLink {
    id: string;
    title: string;
    description: string;
    icon: React.ReactNode;
    onClick: () => void;
    variant?: 'primary' | 'secondary';
}

export interface ILandingPageWidget {
    id: string;
    title: string;
    content: React.ReactNode;
}

interface LandingPageProps {
    title: string;
    subtitle: string;
    description: string;
    heroIcon: React.ReactNode;
    processTitle: string;
    steps?: ILandingPageStep[];
    featureCards?: ILandingPageFeatureCard[];
    quickLinksTitle: string;
    quickLinks: ILandingPageQuickLink[];
    widgets?: ILandingPageWidget[];
    backgroundGradient?: string;
}

function LandingPage({
    title,
    subtitle,
    description,
    heroIcon,
    processTitle,
    steps,
    featureCards,
    quickLinksTitle,
    quickLinks,
    widgets = [],
    backgroundGradient,
}: LandingPageProps) {
    const theme = useTheme();

    const defaultGradient = `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`;

    return (
        <Box
            sx={{
                minHeight: '100vh',
                pb: 4,
            }}
        >
            <Container maxWidth="lg" sx={{ pt: 6, pb: 8 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 6, flexDirection: { xs: 'column', md: 'row' } }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            variant="h2"
                            sx={{
                                fontWeight: 700,
                                mb: 2,
                                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                lineHeight: 1.2,
                            }}
                        >
                            {title}
                        </Typography>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 500,
                                color: 'text.secondary',
                                mb: 3,
                            }}
                        >
                            {subtitle}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: '1.1rem',
                                lineHeight: 1.6,
                                color: 'text.secondary',
                                mb: 4,
                            }}
                        >
                            {description}
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                            {quickLinks.map((link) => (
                                <Paper
                                    key={link.id}
                                    sx={{
                                        flex: 1,
                                        p: 2.5,
                                        borderRadius: 2,
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        border:
                                            link.variant === 'primary'
                                                ? `2px solid ${theme.palette.primary.main}`
                                                : `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                        background:
                                            link.variant === 'primary'
                                                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.dark, 0.08)})`
                                                : theme.palette.background.paper,
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow:
                                                link.variant === 'primary'
                                                    ? `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
                                                    : `0 8px 24px ${alpha(theme.palette.text.primary, 0.1)}`,
                                            borderColor:
                                                link.variant === 'primary'
                                                    ? theme.palette.primary.light
                                                    : theme.palette.primary.main,
                                            background:
                                                link.variant === 'primary'
                                                    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.primary.dark, 0.12)})`
                                                    : alpha(theme.palette.primary.main, 0.05),
                                        },
                                    }}
                                    onClick={link.onClick}
                                >
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Box
                                            sx={{
                                                '& > *': {
                                                    fontSize: 28,
                                                    color: link.variant === 'primary' ? 'primary.main' : 'text.primary',
                                                },
                                            }}
                                        >
                                            {link.icon}
                                        </Box>
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                {link.title}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {link.description}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            ))}
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        {heroIcon}
                    </Box>
                </Box>
            </Container>

            <Container maxWidth="lg" sx={{ mb: 8 }}>
                <Typography
                    variant="h4"
                    sx={{
                        textAlign: 'center',
                        fontWeight: 600,
                        mb: 6,
                        color: 'text.primary',
                    }}
                >
                    {processTitle}
                </Typography>

                {steps && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 4,
                            justifyContent: 'center',
                            pt: 2,
                        }}
                    >
                        {steps.map((step, index) => (
                            <Card
                                key={step.id}
                                sx={{
                                    width: { xs: '100%', sm: 'calc(50% - 16px)', md: 'calc(25% - 24px)' },
                                    minWidth: 250,
                                    textAlign: 'center',
                                    p: 3,
                                    borderRadius: 3,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                    background: alpha(theme.palette.background.paper, 0.8),
                                    backdropFilter: 'blur(10px)',
                                    transition: 'all 0.3s ease',
                                    position: 'relative',
                                    overflow: 'visible',
                                    '&:hover': {
                                        transform: 'translateY(-8px)',
                                        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.2)}`,
                                        borderColor: theme.palette.primary.main,
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: -15,
                                        left: 20,
                                        width: 30,
                                        height: 30,
                                        borderRadius: '50%',
                                        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                        color: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 600,
                                        fontSize: '0.9rem',
                                    }}
                                >
                                    {index + 1}
                                </Box>

                                <Box sx={{ mb: 2, mt: 1 }}>
                                    <Box
                                        sx={{
                                            '& > *': {
                                                fontSize: 48,
                                                color: 'primary.main',
                                            },
                                        }}
                                    >
                                        {step.icon}
                                    </Box>
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                    {step.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {step.description}
                                </Typography>
                            </Card>
                        ))}
                    </Box>
                )}

                {featureCards && (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                            gap: 3,
                            pt: 2,
                        }}
                    >
                        {featureCards.map((card) => (
                            <Card
                                key={card.id}
                                sx={{
                                    cursor: 'pointer',
                                    borderRadius: 3,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                    background: alpha(theme.palette.background.paper, 0.8),
                                    backdropFilter: 'blur(10px)',
                                    transition: 'all 0.3s ease',

                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                                        borderColor: theme.palette.primary.main,
                                    },
                                }}
                                onClick={card.onClick}
                            >
                                <Box sx={{ p: 3 }}>
                                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                                        <Box
                                            sx={{
                                                '& > *': {
                                                    fontSize: 24,
                                                    color: 'primary.main',
                                                },
                                            }}
                                        >
                                            {card.icon}
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {card.title}
                                        </Typography>
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary">
                                        {card.description}
                                    </Typography>
                                </Box>
                            </Card>
                        ))}
                    </Box>
                )}
            </Container>

            {widgets.length > 0 && (
                <Container maxWidth="lg">
                    <Stack spacing={3}>
                        {widgets.map((widget) => (
                            <Paper
                                key={widget.id}
                                sx={{
                                    p: 3,
                                    borderRadius: 3,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                    background: alpha(theme.palette.background.paper, 0.8),
                                    backdropFilter: 'blur(10px)',
                                }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                    {widget.title}
                                </Typography>
                                {widget.content}
                            </Paper>
                        ))}
                    </Stack>
                </Container>
            )}
        </Box>
    );
}

export default LandingPage;
